import React from "react";
import { Link } from "react-router-dom";
import { TrackingLink } from "@/components/TrackingLink";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { isPriorityOrder } from "@/lib/priority";
import { PriorityTag } from "@/components/PriorityTag";

export const BATCH_BADGE = {
  pending: { label: "Pending Shipping", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  shipped: { label: "In transit to warehouse", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  received: { label: "Received", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const day = (s) => (s ? s.slice(0, 10) : null);

// Split a batch's orders by box: boxes sorted by number, unboxed orders last.
export function groupByBox(orders) {
  const map = new Map();
  for (const o of orders) {
    const k = o.box_id || "__loose__";
    if (!map.has(k)) map.set(k, { key: k, box_id: o.box_id || null, number: o.box_number ?? null, date: o.box_date || null, note: o.box_note || "", orders: [] });
    const g = map.get(k);
    g.orders.push(o);
    if (!g.note && o.box_note) g.note = o.box_note;
  }
  return [...map.values()].sort((a, b) => {
    if (!a.box_id !== !b.box_id) return a.box_id ? -1 : 1;
    return (a.date || "").localeCompare(b.date || "") || (a.number || 0) - (b.number || 0);
  });
}

// Header summary for the box column: one box → "BOX n · date", several → "n boxes".
export function boxSummary(groups) {
  const boxes = groups.filter((g) => g.box_id);
  if (boxes.length === 0) return null;
  if (boxes.length === 1) return { number: boxes[0].number, date: boxes[0].date, label: null };
  return { label: `${boxes.length} boxes` };
}

// "↩ Undo shipment" — clears tracking / ship date / box so the orders go back
// to the Shipping Orders queue. Whole tracking batch, or one box (boxId).
export function UndoShipmentButton({ tracking, boxId = null, count, disabled = false, testid }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => api.post("/orders/box-unship", { tracking, box_id: boxId }),
    onSuccess: (r) => { qc.invalidateQueries(); toast.success(`↩ ${r?.data?.count} order(s) back in Shipping Orders`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not undo shipment"),
  });
  const what = boxId ? "this box" : "this whole shipment";
  return (
    <button
      type="button"
      disabled={disabled || m.isPending}
      onClick={(e) => {
        e.stopPropagation();
        if (window.confirm(`Undo ${what} (${count} order(s))?\nTracking # and box will be removed and the orders return to Shipping Orders.`)) m.mutate();
      }}
      data-testid={testid}
      title="Undo shipment → back to Shipping Orders"
      className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded border border-rose-300 text-rose-700 bg-white hover:bg-rose-50 disabled:opacity-40 whitespace-nowrap"
    >
      ↩ {m.isPending ? "Undoing…" : "Undo shipment"}
    </button>
  );
}

// Expanded batch body: orders divided by box, each box labelled + numbered,
// with its own Undo button when `undoTracking` is given (unreceived batches).
export function BoxGroups({ orders, renderOrder, gridCls = CHIP_GRID, undoTracking = null, testidPrefix = "box-group", groupExtra = null }) {
  const groups = groupByBox(orders);
  const single = groups.length === 1 && !groups[0].box_id;
  if (single) return <div className={gridCls}>{orders.map(renderOrder)}</div>;
  // One box only → the batch header already names it; show just the note (if any).
  if (groups.length === 1) {
    const g = groups[0];
    return (
      <div data-testid={`${testidPrefix}-${g.box_id}`}>
        {(g.note || groupExtra) && (
          <div className="mb-1.5 flex items-center gap-2 flex-wrap">
            {groupExtra && groupExtra(g)}
            {g.note && <span className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid={`${testidPrefix}-note-${g.box_id}`}>📝 {g.note}</span>}
          </div>
        )}
        <div className={gridCls}>{g.orders.map(renderOrder)}</div>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {groups.map((g) => (
        <div key={g.key} data-testid={`${testidPrefix}-${g.box_id || "loose"}`}>
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            {groupExtra && g.box_id && groupExtra(g)}
            <span className={`font-mono text-xs font-bold ${g.box_id ? "text-sky-800" : "text-slate-500"}`}>
              {g.box_id ? <>📦 BOX {g.number}{g.date ? <span className="font-normal text-slate-500"> · {g.date}</span> : null}</> : "No box"}
            </span>
            <span className="text-[11px] font-mono text-slate-500">· {g.orders.length} order(s)</span>
            {g.note && <span className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid={`${testidPrefix}-note-${g.box_id}`}>📝 {g.note}</span>}
            {undoTracking && groups.length > 1 && g.box_id && (
              <UndoShipmentButton tracking={undoTracking} boxId={g.box_id} count={g.orders.length} testid={`undo-box-${g.box_id}`} />
            )}
          </div>
          <div className={gridCls}>{g.orders.map(renderOrder)}</div>
        </div>
      ))}
    </div>
  );
}

// Collapsible batch row shared by Shipping Orders + Orders Received.
// `right` renders outside the toggle button so inputs never collapse the row.
export function BatchRow({ tid = "batch", id, open, onToggle, badge, title, tracking = null, carrier = null, box, meta, count, right, children }) {
  const b = BATCH_BADGE[badge] || BATCH_BADGE.shipped;
  return (
    <div className="border border-slate-200 rounded-md bg-white overflow-hidden" data-testid={`${tid}-${id}`}>
      <div className="flex items-center gap-3 flex-wrap pr-4">
        <button
          type="button"
          data-testid={`${tid}-toggle-${id}`}
          onClick={onToggle}
          className="flex-1 min-w-0 px-4 py-3 flex items-center gap-4 hover:bg-slate-50 text-left flex-wrap"
        >
          <span className="text-slate-500 font-mono text-xs w-4">{open ? "▾" : "▸"}</span>
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider border ${b.cls}`} data-testid={`${tid}-status-${id}`}>{b.label}</span>
          {box && <span className="font-mono font-bold text-sky-800 text-sm whitespace-nowrap" data-testid={`${tid}-box-${id}`}>📦 {box.label || <>BOX {box.number} · {box.date}</>}</span>}
          <span className="font-mono font-bold text-slate-900 text-base truncate" title={title} data-testid={`${tid}-tracking-${id}`}>
            {tracking ? <TrackingLink number={tracking} carrier={carrier} testid={`${tid}-tracking-link-${id}`}>{title}</TrackingLink> : title}
          </span>
          <span className="inline-flex items-center gap-3 font-mono text-sm flex-wrap">
            {meta.map((m, i) => (
              <React.Fragment key={m.label}>
                {i > 0 && <span className="text-slate-300">·</span>}
                <span className="inline-flex items-baseline gap-1">
                  <span className="text-[10px] uppercase text-slate-400 tracking-widest">{m.label}</span>
                  <span className={`font-semibold ${m.value ? (m.cls || "text-slate-900") : "text-slate-400"}`}>{day(m.value) || "—"}</span>
                </span>
              </React.Fragment>
            ))}
          </span>
          <span className="ml-auto text-xs font-mono text-slate-500 whitespace-nowrap" data-testid={`${tid}-count-${id}`}>{count} order(s)</span>
        </button>
        {right}
      </div>
      {open && (
        <div className="border-t border-slate-100 bg-slate-50/50 p-3" data-testid={`${tid}-orders-${id}`}>
          {children}
        </div>
      )}
    </div>
  );
}

// PO chip used inside an expanded batch: PO # on the left, status glyph right.
// Warranty / Reorder chips are tinted light red with a small tag.
export function PoChip({ o, stage, glyph, glyphCls, testid, extra }) {
  const priority = isPriorityOrder(o);
  return (
    <Link
      to={`/order/${o.id}`}
      state={{ from: stage }}
      data-testid={testid}
      className={`flex items-center justify-between gap-2 border rounded px-2 py-1.5 transition-colors ${o.ship_hold ? "border-red-300 bg-red-50 hover:border-red-400" : priority ? "border-red-300 bg-red-50 hover:border-red-400" : "border-slate-200 bg-white hover:border-slate-400"}`}
    >
      <span className="font-mono font-semibold text-slate-900 text-sm inline-flex items-center gap-1.5 flex-wrap">{o.po_number}{extra}<PriorityTag order={o} testid={`chip-${o.id}`} />{o.ship_hold && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider text-white bg-amber-500" data-testid={`hold-chip-${o.id}`}>⏸ ON HOLD</span>}</span>
      <span className={`inline-flex items-center px-1.5 py-0 rounded text-[9px] font-medium uppercase border ${glyphCls}`}>{glyph}</span>
    </Link>
  );
}

export const CHIP_GRID = "grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-1.5";

export function useToggleSet() {
  const [set, setSet] = React.useState(() => new Set());
  const toggle = (k) => setSet((prev) => {
    const n = new Set(prev);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  return [set, toggle];
}

// Day an order entered the Shipping stage (last "shipping" history entry).
export function shippingEnteredDay(o) {
  if (o.shipping_entered_at !== undefined) return day(o.shipping_entered_at || o.packed_at || o.updated_at || o.created_at) || "—";
  const hist = Array.isArray(o.status_history) ? o.status_history : [];
  const hit = [...hist].reverse().find((h) => h.status === "shipping" && h.at);
  return day(hit?.at || o.packed_at || o.updated_at || o.created_at) || "—";
}

// Group orders by tracking # keeping the earliest shipped/eta and latest received.
export function groupByTracking(orders) {
  const map = {};
  for (const o of orders) {
    const k = o.shipping_tracking_number || "__untracked__";
    if (!map[k]) {
      map[k] = { key: k, tracking: o.shipping_tracking_number, shipped_at: null, eta: null, received_at: null, orders: [], allReceived: true, anyReceived: false };
    }
    const b = map[k];
    b.orders.push(o);
    if (o.shipping_shipped_at && (!b.shipped_at || o.shipping_shipped_at < b.shipped_at)) b.shipped_at = o.shipping_shipped_at;
    if (o.shipping_eta && (!b.eta || o.shipping_eta < b.eta)) b.eta = o.shipping_eta;
    if (o.received_at) {
      b.anyReceived = true;
      if (!b.received_at || o.received_at > b.received_at) b.received_at = o.received_at;
    } else {
      b.allReceived = false;
    }
  }
  return Object.values(map).map((b) => ({ ...b, box: boxSummary(groupByBox(b.orders)) }));
}
